import { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import * as http from '../../utils/http';
import * as translator from '../../utils/translator';
import { clientConfig } from '../../config';
import { useCartContext, type CartView } from '../../customHooks/useCart';
import { MainTracker } from '../../PixelTrackers/MainTracker';
import { ABTestManager } from '../../utils/ABTestManager';
import { getWlExperimentTrackingProps, registerWlExperimentSuperProperties } from '../../utils/wlCarePlusExperiment';
import { getFlowzzTrackingProps } from '../../utils/flowzzTracking';
import type { PaymentProvider, OrderWindowDetails, CustomerData, OrderAddresses, PaymentContext, AdyenInitData, StripeInitData, CartData } from './paymentTypes';
import { isStripeProvider } from './paymentTypes';
import { SEARCH_PARAM_FOR_NAV_TO_SELECT_TREATMENT } from './paymentConstants';
import { SEARCH_PARAM_FOR_SHIPPING_METHOD_UNAVAILABLE, SHIPPING_METHOD_UNAVAILABLE } from '../../utils/consts';
import { capitalizeFirstChar } from '../../utils/Textutils';
import { getCannabisLimitMessage } from '../ChooseTreatmentVariants/helpersV2';
import { toChargeCurrency } from '../../utils/helpers';
import { useCustomer } from '../../customHooks/useCustomer';
import { setCustomData } from '../../PixelTrackers/KameleoonTracker';


// ============================================================================
// Interfaces
// ============================================================================

interface UsePaymentDataParams {
  country: string;
  language: string;
  hashID: string;
  searchParams: URLSearchParams;
  isRepay: boolean;
  disallowPaymentMethods: string[];
  paymentRefused: boolean;
  onCannabisLimitsBlocked: (message: string) => void;
}

interface UsePaymentDataReturn {
  // Payment Service Providers
  paymentServiceProvider: PaymentProvider;
  adyenInitData: AdyenInitData | null;
  stripeInitData: StripeInitData | null;

  // Order data
  orderState: any;
  orderWindowDetails: OrderWindowDetails;
  shippingFee: number;
  setShippingFee: (fee: number) => void;
  OTCProducts: any[];
  otcDiscount: number;
  currencyRate: string;
  currencyCode: string;
  destinationCountry: string;
  isCannabisCatalog: boolean;
  isTestLabCatalog: boolean;
  testLabSampleType: string | null;
  productsGroupedByPharmacy: any[];
  consultationType: string;
  
  // Payment context for forms
  paymentContext: PaymentContext | null;
  
  // Cart ref for payment
  cartRef: React.MutableRefObject<any>;
  
  // Flow
  flow: string;
  
  // A/B Test - Pay Later
  isEDCatalog: boolean;
  payLaterABTestVariant: string;
  showDelayedCaptureDisclaimer: boolean;

  // A/B Test - SEPA Position
  sepaPositionVariant: string;

  // A/B Test - Stripe Link (DE Rx)
  stripeLinkVariant?: 'A' | 'B';

  // A/B Test - PayPal instructional text
  hidePaypalDisclaimer: boolean;
  
  // Out of stock handler
  handleReviveOutOfStock: (cartData: CartData) => void;
  
  // Cannabis limits
  cannabisLimitsBlocked: boolean;

  // Loading/Error state
  loading: boolean;
  setLoading: (loading: boolean) => void;
  error: number | false;
  setError: (error: number | false) => void;
  
  // Payment types requiring ID verification
  paymentTypesRequiringIdVerification: string[];
  idVerificationPaymentMethodNames: Record<string, string>;
  isCustomerIdVerified: boolean;
  isCarePlusCatalog: boolean;
}


// ============================================================================
// Helpers
// ============================================================================

function reorderSepaAfterPaypal(gatewayResult: any): any {
  const paymentMethods = gatewayResult?.paymentMethods;
  if (!paymentMethods) return gatewayResult;

  const payByBankIdx = paymentMethods.findIndex((pm: any) => pm.type === 'paybybank');
  const paypalIdx = paymentMethods.findIndex((pm: any) => pm.type === 'paypal');

  if (payByBankIdx === -1 || paypalIdx === -1 || payByBankIdx === paypalIdx + 1) {
    return gatewayResult;
  }

  const reordered = [...paymentMethods];
  const [payByBank] = reordered.splice(payByBankIdx, 1);
  const newPaypalIdx = reordered.findIndex((pm: any) => pm.type === 'paypal');
  reordered.splice(newPaypalIdx + 1, 0, payByBank);

  return { ...gatewayResult, paymentMethods: reordered };
}

// ============================================================================
// Hook Implementation - Fetches and manages payment page data
// ============================================================================

export function usePaymentData({country, language, hashID, searchParams, isRepay, disallowPaymentMethods, paymentRefused, onCannabisLimitsBlocked}: UsePaymentDataParams): UsePaymentDataReturn {
  const navigate = useNavigate();
  const { setCartCurrency, updateCart, saveCart } = useCartContext();

  // Provider-specific state
  const [paymentServiceProvider, setPaymentServiceProvider] = useState<PaymentProvider>('adyen');
  const [adyenInitData, setAdyenInitData] = useState<AdyenInitData | null>(null);
  const [stripeInitData, setStripeInitData] = useState<StripeInitData | null>(null);

  // Order data state
  const [orderState, setOrderState] = useState<any>(null);
  const [orderWindowDetails, setOrderWindowDetails] = useState<OrderWindowDetails>({} as OrderWindowDetails);
  const [shippingFee, setShippingFee] = useState(0);
  const [OTCProducts, setOTCProducts] = useState<any[]>([]);
  const [otcDiscount, setOtcDiscount] = useState(0);
  const [currencyRate, setCurrencyRate] = useState('');
  const [currencyCode, setCurrencyCode] = useState('');
  const [destinationCountry, setDestinationCountry] = useState('');
  const [isCannabisCatalog, setIsCannabisCatalog] = useState(false);
  const [isCarePlusCatalog, setIsCarePlusCatalog] = useState(false);
  const [isTestLabCatalog, setIsTestLabCatalog] = useState(false);
  const [testLabSampleType, setTestLabSampleType] = useState<string | null>(null);
  const [productsGroupedByPharmacy, setProductsGroupedByPharmacy] = useState<any[]>([]);

  // A/B Test - Pay Later
  const [isEDCatalog, setIsEDCatalog] = useState(false);
  const [payLaterABTestVariant, setPayLaterABTestVariant] = useState('A');
  const [showDelayedCaptureDisclaimer, setShowDelayedCaptureDisclaimer] = useState(false);

  // A/B Test - SEPA Position
  const [sepaPositionVariant, setSepaPositionVariant] = useState('');

  const { customerState } = useCustomer();
  
  // A/B Test - PayPal instructional text
  const [hidePaypalDisclaimer, setHidePaypalDisclaimer] = useState(false);

  // A/B Test - Stripe Link
  const [stripeLinkVariant, setStripeLinkVariant] = useState<'A' | 'B' | undefined>(undefined);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<number | false>(false);
  
  // Payment types requiring ID verification
  const [paymentTypesRequiringIdVerification, setPaymentTypesRequiringIdVerification] = useState<string[]>([]);
  const [idVerificationPaymentMethodNames, setIdVerificationPaymentMethodNames] = useState<Record<string, string>>({});
  const [isCustomerIdVerified, setIsCustomerIdVerified] = useState(false);
  const [cannabisLimitsBlocked, setCannabisLimitsBlocked] = useState(false);

  // Refs for closure access
  const cartRef = useRef<any>();
  const cartExperimentsRef = useRef<Record<string, string> | null>(null);
  const customerDataRef = useRef<CustomerData | null>(null);
  const catalogRef = useRef<{ mention: string } | null>(null);
  const orderAddressesRef = useRef<OrderAddresses | null>(null);
  const itemTitleRef = useRef('');
  const isOrderOnlyPrescriptionRef = useRef(0);
  const thankyouHashRef = useRef('');
  const isSubscriptionRef = useRef(false);
  const consultationTypeRef = useRef<string>('');
  const flowRef = useRef<string>('');
  const orderPriceRef = useRef<number>(0);
  const currencyRef = useRef<string>('');
  const currencyRateRef = useRef<number>(0);

  // ============================================================================
  // Handle Out of Stock
  // ============================================================================

  const handleReviveOutOfStock = async (cartData: CartData) => {
    if (cartData) {
      saveCart({
        id: Number(cartData.cartID),
        token: cartData.cartToken,
        mention: cartData.mention,
        mixpanel_title: cartData.mixpanelTitle,
        products: [],
        timekitBooking: cartData.timekitBooking,
      });
    }

    const viewLanguage = translator.getLanguageByCountry(cartData.country);
    navigate({
      pathname: `/select_treatment/${cartData.country.toLowerCase()}/${viewLanguage}/${cartData.mention}`,
      search: `?ec=${SEARCH_PARAM_FOR_NAV_TO_SELECT_TREATMENT}`,
    });
  };

  // The order's shipping method closed — the server rebuilt a cart from the order (no method preselected);
  // the customer picks an available one on Shipping. rc=true: Shipping submit cancels the old order like payment Back does.
  const handleShippingMethodUnavailable = (cartData: CartData | undefined, cartView: { cart: CartView; products: CartView['products']; timekitBooking: any } | undefined, restoredByCartID: number | null | undefined) => {
    if (!cartData || !cartView) {
      navigate({ pathname: `/${country}/account/my-orders` });
      return;
    }
    // Same shape payment Back's restoreCartFromOrder saves: the full cart with its products
    saveCart({
      ...cartView.cart,
      mention: cartData.mention,
      mixpanel_title: cartData.mixpanelTitle,
      products: cartView.products,
      timekitBooking: cartView.timekitBooking,
    });
    window.eventsTracking.setData('cart_mention', cartData.mention, true);
    window.eventsTracking.setData('restored_by_cart_id', restoredByCartID, true);
    navigate(
      { pathname: `/shipping/${country}/${language}`, search: `?rc=true&ec=${SEARCH_PARAM_FOR_SHIPPING_METHOD_UNAVAILABLE}` },
      { replace: true }
    );
  };

  // HL-12175: one event per revive attempt; original_shipping_method e.g. "urbify_same_day"
  const trackReviveResult = (reviveResult: 'revived' | 'redirected_to_shipping' | 'out_of_stock', originalShippingMethod: string | null) => {
    MainTracker.track('custom', 'Funnel Revived Order', { revive_result: reviveResult, original_shipping_method: originalShippingMethod });
  };

  // ============================================================================
  // Main Data Fetching Effect
  // ============================================================================

  useEffect(() => {
    // Check if the order has already been paid
    if (window.eventsTracking.getData('last_paid_order') === hashID && !isRepay) {
      setError(true as any);
      setLoading(false);
      return;
    }

    // Variant set on shipping-page load; still used below to gate the Express Checkout Link wallet button.
    const stripeLinkVariantFromStorage = localStorage.getItem('abtest_stripe_link');

    // Read from the URL for tracking only - handleGet no longer consumes this.
    const mailCodeParam = searchParams.get('mail_code');
    http.getFromServer(`payment_v2/${country}/${language}/${hashID}?disallow=${disallowPaymentMethods}`,
      [http.MIDDLEWARES.CUSTOMER_ID])
      .then(async (response) => {
        const data = response.data;
        
        if (data.status === 'recent_pending_payment') {
          // Do nothing - continue with normal flow when recent pending payment is detected
          return;
        }

        const prescriptionProduct = data.prescriptionProduct;
        const currencyRateValue = data.order.currency_rate;
        const cartView = data.cartView;

        consultationTypeRef.current = data.consultationType;
        flowRef.current = data.order.flow;
        customerDataRef.current = data.customerData;
        orderAddressesRef.current = data.orderAddresses;
        catalogRef.current = data.catalog;
        itemTitleRef.current = prescriptionProduct.short_title || prescriptionProduct.title;
        isSubscriptionRef.current = data.isSubscription;
        isOrderOnlyPrescriptionRef.current = data.order.only_prescription;
        thankyouHashRef.current = data.thankyouHash;
        orderPriceRef.current = data.totalOrdersPrice;
        currencyRef.current = data.currency;
        currencyRateRef.current = Number(currencyRateValue);
        cartRef.current = { ...cartView.cart, products: cartView.products, timekitBooking: cartView.timekitBooking };
        cartExperimentsRef.current = data.cartExperiments || null;

        updateCart({...cartRef.current, doctor: cartView.doctor});
        setPaymentServiceProvider(data.paymentServiceProvider);
        setIsCarePlusCatalog(data.isCarePlus === true);
        setIsCannabisCatalog(data.isCannabisCatalog);
        setIsTestLabCatalog(data.isTestLabCatalog);
        setTestLabSampleType(data.testLabSampleType ?? null);
        setIsCustomerIdVerified(data.isCustomerIdVerified ?? false);
        setProductsGroupedByPharmacy(data.productsGroupedByPharmacy || []);
        setOtcDiscount(data.otcDiscount);
        setOTCProducts(data.OTCProducts);
        setDestinationCountry(data.country);
        setCartCurrency(data.countryCurrenciesByID[country?.toLowerCase()]);
        setOrderState(data.order);
        setCurrencyCode(data.currency);
        setCurrencyRate(currencyRateValue);
        // Kameleoon (HL-12604): same price * currency_rate the app already reports as the EUR order amount.
        setCustomData("order_total_eur", Math.round(Number(data.totalOrdersPrice) * (Number(currencyRateValue) || 1) * 100) / 100);
        setShippingFee(cartView.cart.custom_shipping_fee);
        setOrderWindowDetails({
          itemImageUrl: clientConfig.IMAGES_URL + prescriptionProduct.img_url,
          itemTitle: itemTitleRef.current,
          itemQuantity: prescriptionProduct.custom_quantity_title,
          treatmentFee: prescriptionProduct.treatment_fee,
          Price: prescriptionProduct.price,
          serviceFee: prescriptionProduct.service_fee,
          // FR breakdown: any subsidized product in the order (not only the primary Rx line)
          isSubsidized: Boolean(prescriptionProduct.is_subsidized) || Boolean(cartView.products?.some((p: { is_subsidized?: boolean | number }) => Boolean(Number(p.is_subsidized)))),
          shippingFee: prescriptionProduct.shipping_fee,
          discount: prescriptionProduct.subscription_discount,
          bonusValue: prescriptionProduct.bonus_value,
          // is_express_shipping comes back from the API as 0/1 (a number), but the type is boolean.
          // Without coercion, `{isExpressShipping && (...)}` in the cart renders a stray literal 0.
          isExpressShipping: Boolean(data.order.is_express_shipping),
          isSelfPickup: data.order.is_self_pickup,
          isCannabisIDAddedToOrder: data.order.is_cannabis_card_required,
          disclaimer: (data.isCannabisCatalog && !data.order.is_self_pickup) ? translator.translate(language, 'default', 'OrderWindow/high_demand_disclaimer') : '',
          cannabisIdCardCost: data.cannabisIdCardCost,
          onlyPrescription: data.order.only_prescription,
          orderShippingType: data.orderShippingType
        });

        // Handle returning customer A/B test
        const isReturningCustomer = data.isReturningCustomer;
        let reorderedPaymentMethods = false;

        if (isReturningCustomer) {
          const storageKey = 'dynamicPaymentMethodTestGroup';
          const testGroup = localStorage.getItem(storageKey);
          
          if (testGroup === 'B') {
            reorderedPaymentMethods = true;
            MainTracker.track('custom', 'Returning User Viewed Payment', { group: 'B' });
          } else {
            MainTracker.track('custom', 'Returning User Viewed Payment', { group: 'A' });
          }
        }

        // Determine A/B test variant for pay later test using ABTestManager
        const isEDCatalogFromURL = searchParams.get("cat") === "erectile_dysfunction";
        setIsEDCatalog(isEDCatalogFromURL);
        
        let payLaterABTestVariantValue = 'A'; // Default to control
        if (data.payLaterABTestVariable) {
          const abTestValue = Number(data.payLaterABTestVariable);
          if (abTestValue >= 0 && abTestValue <= 1) {
            // Use ABTestManager for 50/50 split
            const abTest = new ABTestManager({
              testName: 'pay_later',
              variants: ['A', 'B'],
              weights: [1 - abTestValue, abTestValue]
            });
            payLaterABTestVariantValue = abTest.getVariant();
          }
        }
        setPayLaterABTestVariant(payLaterABTestVariantValue);
        
        // Set A/B test variant as super property for all tracking events
        MainTracker.superProperties({ ED_DueNow_Test: payLaterABTestVariantValue });
        
        // Calculate whether to show Pay Later A/B Test Section
        // Use URL parameter to check if it's ED catalog instead of server response
        const shouldShowPayLaterABTest = isEDCatalogFromURL && 
                          !data.order.only_prescription && 
                          payLaterABTestVariantValue === 'B';
        
        // Show Pay Later A/B Test Section based only on A/B test conditions (not payment method)
        setShowDelayedCaptureDisclaimer(shouldShowPayLaterABTest);

        if (data.cannabisLimits?.isBlocked) {
          setCannabisLimitsBlocked(true);
          onCannabisLimitsBlocked(getCannabisLimitMessage(data.cannabisLimits, language, 'payment'));
          return;
        }

        // Cannabis premium PayPal AB test (persistent via ABTestManager / localStorage).
        // For cannabis premium: AB test controls whether PayPal appears as a payment method.
        //   Variant A (or test disabled): PayPal removed from PMs, no verification UI needed.
        //   Variant B: PayPal kept, verification types from server apply (disclaimer + Jumio modal).
        // For non-cannabis-premium: no AB test, PayPal is a normal PM with no verification.
        let effectiveVerificationTypes: string[] = data.paymentTypesRequiringIdVerification ?? [];
        let effectiveVerificationNames: Record<string, string> = { ...(data.idVerificationPaymentMethodNames ?? {}) };

        setPaymentTypesRequiringIdVerification(effectiveVerificationTypes);
        setIdVerificationPaymentMethodNames(effectiveVerificationNames);

        // A/B Test: PayPal instructional text removal (cannabis users, 10/10/80 split by last digit of customer ID)
        const customerID = customerState?.customerID;
        const isCannabis = data.isCannabisCatalog;
        if (isCannabis && customerID) {
          const ppTextTest = new ABTestManager({
            testName: 'pp_instructional_text_test',
            variants: ['control', 'no_text', 'default'],
            seed: customerID,
            assignmentFn: (seed) => {
              const lastDigit = Number(seed?.toString()?.slice(-1));
              if (lastDigit === 0) return 'control';
              if (lastDigit === 1) return 'no_text';
              return 'default';
            },
          });
          const ppTextVariant = ppTextTest.getVariant();
          if (ppTextVariant !== 'default') {
            MainTracker.superProperties({ pp_instructional_text_test: ppTextVariant });
          }
          if (ppTextVariant === 'no_text') {
            setHidePaypalDisclaimer(true);
          }
        }

        // Set provider-specific data
        let sepaPositionVariantValue = '';
        if (data.paymentServiceProvider === 'adyen') {
          // A/B Test - SEPA Position: only for German users on Adyen
          if (country?.toUpperCase() === 'DE') {
            const sepaPositionAbTestValue = data.sepaPositionABTestVariable != null
              ? Number(data.sepaPositionABTestVariable)
              : 0.5; // default to 50/50 if variable not in DB
            if (sepaPositionAbTestValue >= 0 && sepaPositionAbTestValue <= 1) {
              const sepaPositionTest = new ABTestManager({
                testName: 'sepa_position',
                variants: ['control', 'variation'],
                weights: [1 - sepaPositionAbTestValue, sepaPositionAbTestValue],
              });
              sepaPositionVariantValue = sepaPositionTest.getVariant();
            }
            setSepaPositionVariant(sepaPositionVariantValue);
            MainTracker.superProperties({ sepa_position_test: sepaPositionVariantValue });
          }

          // Reorder payment methods for SEPA position test variation: move paybybank after paypal
          let gatewayResultForInit = data.gatewayResult;
          let reorderedGatewayResultForInit = data.reorderedGatewayResult;
          if (sepaPositionVariantValue === 'variation') {
            gatewayResultForInit = reorderSepaAfterPaypal(data.gatewayResult);
            if (data.reorderedGatewayResult) {
              reorderedGatewayResultForInit = reorderSepaAfterPaypal(data.reorderedGatewayResult);
            }
          }

          setAdyenInitData({
            gatewayResult: gatewayResultForInit,
            reorderedGatewayResult: reorderedGatewayResultForInit,
            clientKey: data.clientKey,
            environment: data.environment,
            isSubscription: isSubscriptionRef.current,
            orderAddresses: data.orderAddresses,
            customerData: data.customerData,
            reorderedPaymentMethods,
          });
        } else if (isStripeProvider(data.paymentServiceProvider)) {
          setStripeInitData({
            gatewayResult: data.gatewayResult,
            stripePublicKey: data.publicKey,
            customerSessionSecret: data.gatewayResult.customerSessionSecret,
            isSubscription: isSubscriptionRef.current,
            customerData: data.customerData,
            orderAddresses: data.orderAddresses,
          });
        }

        const stripeLinkBucketed = stripeLinkVariantFromStorage === 'A' || stripeLinkVariantFromStorage === 'B';
        if (stripeLinkBucketed) {
          setStripeLinkVariant(stripeLinkVariantFromStorage as 'A' | 'B');
          MainTracker.superProperties({ stripe_link_test: stripeLinkVariantFromStorage });
        }

        const isCarePlusPayment = Number(cartRef.current?.care_plus_plan_fee ?? 0) > 0;
        const carePlusPaymentProps = isCarePlusPayment ? {
          // HL-12122: V3 Monthly is not a Care+ tier — only WL2 Care+ orders carry `tier`.
          ...(cartRef.current?.care_plus_plan_type === "monthly" ? {} : { tier: cartRef.current?.care_plus_plan_tier ?? null }),
          plan_duration: cartRef.current?.care_plus_plan_period_months ?? null,
          is_one_time: !cartRef.current?.subscription_plan_id,
          // HL-12264: UK prices in GBP (the order's currency).
          plan_fee: toChargeCurrency(Number(cartRef.current?.care_plus_plan_fee ?? 0), currencyRateRef.current, country),
          medication_price: toChargeCurrency((cartRef.current?.products?.find((p: any) => !p.is_addon) ?? cartRef.current?.products?.[0])?.item_price ?? null, currencyRateRef.current, country),
          // HL-12264: no plan_variant on UK V3; every other country keeps it.
          ...(country?.toUpperCase() === "GB" ? {} : { plan_variant: null }),
          plan_type: cartRef.current?.care_plus_plan_type ?? null, // HL-12122
        } : {};
        // HL-10959: Care+ A/B arm + entry_point through payment
        registerWlExperimentSuperProperties();
        const viewPaymentPageProperties = {
          searchParamas: Object.fromEntries(searchParams.entries()),
          ED_DueNow_Test: payLaterABTestVariantValue,
          ...(stripeLinkBucketed && { stripe_link_test: stripeLinkVariantFromStorage }),
          ...(sepaPositionVariantValue && { sepa_position_test: sepaPositionVariantValue }),
          ...(data.paymentServiceProvider && { "Payment Service Provider": capitalizeFirstChar(data.paymentServiceProvider) }),
          ...carePlusPaymentProps,
          ...getWlExperimentTrackingProps(),
        };
        if (data.reviveFlag) trackReviveResult('revived', data.originalShippingMethod);

        // Track page view with A/B test variants
        // HL-12122: one shared event for every funnel version (no separate "V3 CarePlus" name).
        MainTracker.track("pv", "View Payment Page", { ...viewPaymentPageProperties, ...getFlowzzTrackingProps() });

        if (mailCodeParam === 'payment_retry_grace_period_failure') {
          MainTracker.track('custom', 'Payment Retry Link Clicked', {
            order_id: hashID,
            original_payment_method: data.lastPaymentMethodCode,
            ...viewPaymentPageProperties,
          });
        }
      })
      .catch((err) => {
        if (err.response?.data?.code === 'invalid_order_status_for_payment') {
          navigate({ pathname: `/${country}/account/my-orders` });
          return;
        }
        if (err.response?.data?.status === SHIPPING_METHOD_UNAVAILABLE) {
          if (err.response?.data?.reviveFlag) trackReviveResult('redirected_to_shipping', err.response?.data?.originalShippingMethod);
          handleShippingMethodUnavailable(err.response?.data?.cartData, err.response?.data?.cartView, err.response?.data?.restoredByCartID);
          return;
        }
        if (err.response?.data?.logLevel === 'warn') {
          if (err.response?.data?.status === 'out_of_stock') {
            trackReviveResult('out_of_stock', err.response?.data?.originalShippingMethod);
            handleReviveOutOfStock(err.response?.data?.cartData);
            return;
          }
          window.clientLogger.warn('warning in get payment', err);
          setError(400);
        } else {
          window.clientLogger.error('error in get payment', err);
          setError(400);
        }
      })
      .finally(() => setLoading(false));
  }, [paymentRefused]);

  // ============================================================================
  // Build Payment Context
  // ============================================================================

  const paymentContext = useMemo<PaymentContext | null>(() => {
    if (!customerDataRef.current || !catalogRef.current) return null;
    return {
      hashID,
      country,
      language,
      currencyCode,
      currency: currencyRef.current,
      orderPrice: orderPriceRef.current,
      currencyRate: currencyRateRef.current,
      itemTitle: itemTitleRef.current,
      force3DS: searchParams.get('force_3ds'),
      isRepay,
      mailCode: searchParams.get('mail_code'),
      orderIds: cartRef.current?.products?.map((p: any) => p.order_id) || [],
      orderHashIDs: cartRef.current?.products?.map((p: any) => p.hash_id).filter(Boolean) || [],
      isCannabisCatalog,
      isTestLabCatalog,
      thankyouHash: thankyouHashRef.current,
      customerData: customerDataRef.current,
      catalog: catalogRef.current,
      isOrderOnlyPrescription: isOrderOnlyPrescriptionRef.current,
      paymentServiceProvider,
      sepaPositionVariant,
      stripeLinkVariant,
      cartExperiments: cartExperimentsRef.current,
    };
  }, [hashID, country, language, currencyCode, isCannabisCatalog, isTestLabCatalog, paymentServiceProvider, isRepay, searchParams, sepaPositionVariant, stripeLinkVariant]);

  // ============================================================================
  // Return
  // ============================================================================

  return {
    loading,
    setLoading,
    error,
    setError,
    paymentServiceProvider,
    orderState,
    orderWindowDetails,
    shippingFee,
    setShippingFee,
    OTCProducts,
    otcDiscount,
    currencyRate,
    currencyCode,
    destinationCountry,
    isCannabisCatalog,
    isTestLabCatalog,
    testLabSampleType,
    productsGroupedByPharmacy,
    adyenInitData,
    stripeInitData,
    paymentContext,
    cartRef,
    isEDCatalog,
    payLaterABTestVariant,
    showDelayedCaptureDisclaimer,
    sepaPositionVariant,
    stripeLinkVariant,
    handleReviveOutOfStock,
    cannabisLimitsBlocked,
    hidePaypalDisclaimer,
    consultationType: consultationTypeRef.current,
    flow: flowRef.current,
    paymentTypesRequiringIdVerification,
    idVerificationPaymentMethodNames,
    isCustomerIdVerified,
    isCarePlusCatalog,
  };
}